import React from 'react';
import { useNavigate } from 'react-router-dom';

export default function CompanyPolicyScreen() {
  const navigate = useNavigate();

  const previewPolicies = [
    {
      title: 'Code of Conduct & Ethics',
      code: 'POL-ETH-01',
      description: 'Core organizational values, professional standards, integrity, and anti-corruption guidelines.',
      icon: 'gavel',
      color: 'text-primary',
      bg: 'bg-primary/10',
    },
    {
      title: 'Workplace Health & Safety',
      code: 'POL-WHS-02',
      description: 'Emergency response, occupational health, ergonomics, and accident prevention protocols.',
      icon: 'health_and_safety',
      color: 'text-emerald-600',
      bg: 'bg-emerald-500/10',
    },
    {
      title: 'Information Security & Data Privacy',
      code: 'POL-SEC-03',
      description: 'Confidentiality, intellectual property, password norms, and GDPR/IT Act compliance.',
      icon: 'security',
      color: 'text-indigo-600',
      bg: 'bg-indigo-500/10',
    },
    {
      title: 'POSH & Anti-Harassment Guidelines',
      code: 'POL-POSH-04',
      description: 'Equal opportunity, prevention of sexual harassment, grievance redressal, and ICC committee.',
      icon: 'shield_person',
      color: 'text-rose-600',
      bg: 'bg-rose-500/10',
    },
    {
      title: 'Leave & Remote Work Norms',
      code: 'POL-HR-05',
      description: 'Hybrid working models, attendance regularization, public holidays, and sabbatical policies.',
      icon: 'work_history',
      color: 'text-amber-600',
      bg: 'bg-amber-500/10',
    },
    {
      title: 'IT Asset & Device Usage Policy',
      code: 'POL-IT-06',
      description: 'Laptops, cloud services, software license usage, and remote access VPN guidelines.',
      icon: 'devices',
      color: 'text-sky-600',
      bg: 'bg-sky-500/10',
    },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Top Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/15 pb-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Company Policy
          </h1>
          <p className="mt-1.5 text-xs text-on-surface-variant font-medium">
            Official employee handbook, governance compliance, code of conduct, and organizational guidelines
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/support')}
            className="inline-flex items-center gap-2 rounded-xl bg-surface-container-high px-4 py-2.5 text-xs font-bold text-slate-700 shadow-xs hover:bg-surface-container-highest transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">support_agent</span>
            <span>Contact HR Desk</span>
          </button>
        </div>
      </section>

      {/* Main Under Construction Hero Card */}
      <div className="relative overflow-hidden rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-8 sm:p-12 shadow-xs text-center">
        {/* Subtle Decorative Background Element */}
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-amber-500/5 blur-3xl pointer-events-none" />

        <div className="mx-auto flex max-w-2xl flex-col items-center">
          {/* Animated Construction Badge */}
          <div className="relative mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-primary/10 text-primary shadow-inner">
            <span className="material-symbols-outlined text-4xl">policy</span>
            <span className="absolute -top-1.5 -right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-amber-500 text-white shadow-xs">
              <span className="material-symbols-outlined text-base">construction</span>
            </span>
          </div>

          <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-3.5 py-1 text-xs font-bold text-amber-700 border border-amber-500/25 mb-4">
            <span className="material-symbols-outlined text-sm">hourglass_top</span>
            <span>Page Under Construction</span>
          </div>

          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-on-surface tracking-tight">
            Company Policy Portal is Coming Soon
          </h2>

          <p className="mt-3 text-xs sm:text-sm text-slate-600 leading-relaxed max-w-xl">
            Our HR & Legal Compliance teams are currently digitizing and publishing the complete handbook, employee charter, compliance mandates, and organizational policy repository.
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/my-profile')}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-xs font-bold text-on-primary shadow-xs hover:bg-primary/90 transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-base">account_circle</span>
              <span>View My Profile</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard/personal')}
              className="inline-flex items-center gap-2 rounded-xl border border-outline-variant/30 bg-surface-container-low px-5 py-2.5 text-xs font-bold text-slate-700 hover:bg-surface-container-high transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-base">dashboard</span>
              <span>Back to Dashboard</span>
            </button>
          </div>
        </div>
      </div>

      {/* Upcoming Policy Sections Preview Grid */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-extrabold uppercase tracking-wider text-black">
              Upcoming Policy Handbooks
            </h3>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">
              The following policy documents will be accessible directly once published
            </p>
          </div>
          <span className="rounded-lg bg-surface-container-high px-2.5 py-1 text-[11px] font-bold text-slate-600">
            6 Modules in Review
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {previewPolicies.map((item) => (
            <div
              key={item.code}
              className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-5 shadow-xs transition-all hover:shadow-sm flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${item.bg} ${item.color}`}>
                    <span className="material-symbols-outlined text-xl">{item.icon}</span>
                  </div>
                  <span className="rounded-md bg-surface-container-high px-2 py-0.5 text-[10px] font-mono font-bold text-slate-600">
                    {item.code}
                  </span>
                </div>
                <h4 className="text-sm font-bold text-on-surface">{item.title}</h4>
                <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </div>

              <div className="mt-4 pt-3 border-t border-outline-variant/15 flex items-center justify-between text-[11px]">
                <span className="inline-flex items-center gap-1 font-semibold text-amber-700">
                  <span className="material-symbols-outlined text-xs">pending</span>
                  <span>Draft in review</span>
                </span>
                <span className="text-slate-400 font-medium">Coming Soon</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
